import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { FaSignal } from 'react-icons/fa';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { extractAccessToken } from '../helpers/jwt';

type N8NEnvironment = {
    n8n_api_url: string;
    n8n_ping_api_url: string;
    n8n_ping_sid: string;
    n8n_ping_company_code: string;
    n8n_ping_web_id: string;
    n8n_ping_ip_address: string;
    n8n_ping_module: string;
    n8n_ping_interval: string;
    fallback_message: string;
};

type PingResult = {
    status: 'success';
    data: {
        latency: number;
    };
};

type PingError = {
    status: 'error';
    message: string;
};

const isPingResult = (obj: any): obj is PingResult =>
    'status' in obj && obj.status === 'success' && 'data' in obj;

const isPingError = (obj: any): obj is PingError =>
    'status' in obj && obj.status === 'error' && 'message' in obj;

const PING_URL = 'https://app-pool-api.sonarinno.com';
const PING_INTERVAL = 60 * 1000;

export type PingProps = {
    user: string;
};

export default function Ping(props: PingProps) {
    const { user } = props;

    const [ping, setPing] = useState<number>();
    const [isRunning, setIsRunning] = useState(false);

    const { data: n8nEnvironment, isLoading } = useQuery({
        queryKey: ['n8n'],
        queryFn: async () => axios.get<N8NEnvironment>('/v1/n8n'),
        select: (response) => response.data,
        refetchOnWindowFocus: false,
    });

    const worker = useMemo(
        () => new Worker(new URL('../workers/ping.worker.js', import.meta.url)),
        []
    );

    useEffect(() => {
        if (isLoading) return;
        if (!n8nEnvironment) return;

        worker.postMessage({ url: PING_URL });
        worker.onmessage = (event) => {
            const data = event.data;
            if (!data) {
                setPing(-1);
                return;
            }

            if (isPingError(data) || !isPingResult(data)) {
                setPing(-1);
                return;
            }

            setPing(data.data.latency);

            fetch(n8nEnvironment.n8n_ping_api_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sid: n8nEnvironment.n8n_ping_sid,
                    company_code: n8nEnvironment.n8n_ping_company_code,
                    web_id: n8nEnvironment.n8n_ping_web_id,
                    ip_address: n8nEnvironment.n8n_ping_ip_address,
                    user_id: 
                        extractAccessToken().citizen_id ||
                        extractAccessToken().user_id ||
                        extractAccessToken().username,
                    module: n8nEnvironment.n8n_ping_module,
                    ping_time: data.data.latency,
                }),
            });
        };

        const interval = parseInt(n8nEnvironment.n8n_ping_interval);

        const timer = setInterval(
            () => worker.postMessage({ url: PING_URL }),
            Number.isNaN(interval) || interval === 0 ? PING_INTERVAL : interval
        );

        return () => {
            clearInterval(timer);
            worker.terminate();
        };
    }, [isLoading, n8nEnvironment]);

    if (!ping) return null;

    const pingColor =
        ping < 150
            ? '#2B7A0B'
            : ping < 200
            ? '#B45309'
            : ping < 400
            ? '#9A3412'
            : '#991B1B';

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginRight: '4px',
            }}
        >
            <FaSignal
                size={20}
                color={pingColor}
            />
            <span
                style={{ color: pingColor }}
                className="text-lg font-medium"
            >
                {ping.toFixed(2)} ms
            </span>
        </div>
    );
}
